import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
  api,
  buildQueryParams,
  getCreatedToastMessage,
  getErrorToastMessage,
  URLs,
} from '@/libs/api'
import { QUERY_KEYS, STALE_TIME } from '@/libs/constants'
import type {
  ApiResponse,
  HookParams,
  Product,
  ProductListData,
  ProductListParams,
} from '@/libs/interfaces'
import { productsApi } from '@/services/api'

export function useProducts({ params, enabled }: HookParams<ProductListParams>) {
  return useQuery<Product[]>({
    queryKey: QUERY_KEYS.products.list(params),
    queryFn: async () => {
      const { orgID, storeID, ...queryParams } = params
      const query = buildQueryParams(queryParams)
      return await api
        .get<ApiResponse<ProductListData>>(URLs.Products.list(orgID, storeID, query))
        .then((res) => res?.data?.data?.products ?? [])
    },
    enabled: (enabled ?? true) && !!params.orgID && !!params.storeID,
    staleTime: STALE_TIME['24Hours'],
  })
}

export function useInfiniteProducts({ params, enabled }: HookParams<ProductListParams>) {
  return useInfiniteQuery({
    queryKey: QUERY_KEYS.products.infinite(params),
    queryFn: ({ pageParam }) =>
      productsApi.list({
        ...params,
        cursor: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.next || undefined,
    getPreviousPageParam: (firstPage) => firstPage.prev || undefined,
    enabled: (enabled ?? true) && !!params.orgID,
    initialPageParam: undefined as string | undefined,
    staleTime: STALE_TIME['24Hours'],
  })
}

export function useProductByID({ params, enabled }: HookParams<Partial<Product>>) {
  return useQuery<Product>({
    queryKey: QUERY_KEYS.products.id(params.productID || ''),
    queryFn: () => productsApi.getById(params),
    enabled: (enabled ?? true) && !!params.productID,
    staleTime: STALE_TIME['24Hours'],
  })
}

export function useCreateProduct() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: productsApi.create,
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.products.all })
      toast.success(getCreatedToastMessage(result, t('products.productAdded')))
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

export function useUpdateProduct() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: productsApi.update,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.products.all })
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

export function useDeleteProduct() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: productsApi.delete,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.products.all })
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}
